import { useState, useCallback, useEffect, createContext, useContext } from 'react';

/**
 * Theme options
 * - 'light': Light mode
 * - 'dark': Dark mode (GitHub Dark Dimmed)
 * - 'system': Follow system preference
 */
export type Theme = 'light' | 'dark' | 'system';

const THEME_STORAGE_KEY = 'alpro-theme';
const VALID_THEMES: Theme[] = ['light', 'dark', 'system'];

/**
 * Get stored theme from localStorage
 */
function getStoredTheme(): Theme | null {
  if (typeof window === 'undefined') {return null;}
  
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (stored && VALID_THEMES.includes(stored as Theme)) {
      return stored as Theme;
    }
  } catch {
    // localStorage not available
  }
  return null;
}

/**
 * Store theme in localStorage
 */
function setStoredTheme(theme: Theme): void {
  if (typeof window === 'undefined') {return;}
  
  try {
    localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    // localStorage not available
  }
}

/**
 * Get system preference using matchMedia
 */
function getSystemTheme(): 'light' | 'dark' {
  if (typeof window === 'undefined') {return 'light';}
  
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/**
 * Resolve actual theme from preference
 * If 'system', returns the actual system theme
 */
function resolveTheme(theme: Theme): 'light' | 'dark' {
  if (theme === 'system') {
    return getSystemTheme();
  }
  return theme;
}

/**
 * Apply theme to document root
 * Adds/removes 'dark' class and sets data-theme attribute
 */
function applyTheme(theme: 'light' | 'dark'): void {
  if (typeof document === 'undefined') {return;}
  
  const root = document.documentElement;
  root.classList.remove('light', 'dark');
  root.classList.add(theme);
  root.setAttribute('data-theme', theme);
}

/**
 * Get initial theme preference
 * Priority: localStorage > defaultTheme > system
 */
function getInitialTheme(defaultTheme: Theme = 'system'): Theme {
  const stored = getStoredTheme();
  if (stored) {return stored;}
  return defaultTheme;
}

// ============================================================================
// Theme Context
// ============================================================================

export interface ThemeContextValue {
  theme: Theme;
  resolvedTheme: 'light' | 'dark';
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
}

export const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

/**
 * Hook that provides theme state management logic
 * Used internally by ThemeProvider
 */
export function useThemeProvider(defaultTheme: Theme = 'system'): ThemeContextValue {
  const [theme, setThemeState] = useState<Theme>(() => getInitialTheme(defaultTheme));
  const [resolvedTheme, setResolvedTheme] = useState<'light' | 'dark'>(() => resolveTheme(theme));

  // Apply theme on mount and when theme changes
  useEffect(() => {
    const resolved = resolveTheme(theme);
    setResolvedTheme(resolved);
    applyTheme(resolved);
  }, [theme]);

  // Listen for system preference changes when using 'system' theme
  useEffect(() => {
    if (theme !== 'system') {return;}
    
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleChange = (e: MediaQueryListEvent) => {
      const newTheme = e.matches ? 'dark' : 'light';
      setResolvedTheme(newTheme);
      applyTheme(newTheme);
    };
    
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [theme]);

  // Prevent flash by applying theme before hydration
  useEffect(() => {
    const resolved = resolveTheme(theme);
    applyTheme(resolved);
  }, [theme]);

  const setTheme = useCallback((newTheme: Theme) => {
    setThemeState(newTheme);
    setStoredTheme(newTheme);
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeState((current) => {
      // If system, switch to opposite of current resolved theme
      if (current === 'system') {
        const resolved = resolveTheme(current);
        const next = resolved === 'dark' ? 'light' : 'dark';
        setStoredTheme(next);
        return next;
      }
      // Otherwise toggle between light and dark
      const next = current === 'dark' ? 'light' : 'dark';
      setStoredTheme(next);
      return next;
    });
  }, []);

  return { theme, resolvedTheme, setTheme, toggleTheme };
}

/**
 * Hook to access theme context
 * Must be used within a ThemeProvider
 */
export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  
  return context;
}

// ============================================================================
// Script to prevent flash on page load
// ============================================================================

/**
 * Returns a script string to inject in HTML head
 * This prevents the flash of unstyled content on page load
 */
export const themeScript = `
(function() {
  try {
    var theme = localStorage.getItem('alpro-theme');
    var systemDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    var isDark = theme === 'dark' || (theme === 'system' && systemDark) || (!theme && systemDark);
    
    document.documentElement.classList.remove('light', 'dark');
    if (isDark) {
      document.documentElement.classList.add('dark');
      document.documentElement.setAttribute('data-theme', 'dark');
    } else {
      document.documentElement.classList.add('light');
      document.documentElement.setAttribute('data-theme', 'light');
    }
  } catch (e) {}
})();
`;
